/* ============================================================
   Wiredan/Agritrade AuthContext - Clean & Production Ready
   Path: src/context/AuthContext.jsx
   Status: STABLE | AUTO-SYNC USER IDENTITY | ZERO MISMATCH GUARANTEED
   ============================================================ */

import React, { createContext, useState, useContext, useEffect, useCallback } from "react";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // 🛡️ Instant Synchronous Hydration from LocalStorage on Boot
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem("agritrade_user");
      if (savedUser) {
        const parsed = JSON.parse(savedUser);
        const resolvedWallet = parsed.wallet_address || parsed.sidra_wallet || parsed.sidra_wallet_address || "";
        const computedDan = Number(parsed.dan_balance || 0);
        const premiumFlag = (computedDan >= 2000) ? 1 : Number(parsed.is_premium || 0);
        return {
          ...parsed,
          id: parsed.id || parsed.uuid || localStorage.getItem("userId") || "",
          uuid: parsed.uuid || parsed.id || localStorage.getItem("userUuid") || "",
          wallet_address: resolvedWallet,
          sidra_wallet: resolvedWallet,
          sidra_wallet_address: resolvedWallet,
          kyc_verified: Number(parsed.kyc_verified) || 0,
          kyc_status: parsed.kyc_status || "unverified",
          balance: Number(parsed.balance) || 0,
          dan_balance: computedDan,
          sda_balance: Number(parsed.sda_balance) || 0,
          is_premium: premiumFlag,
          can_trade_high_value: premiumFlag,
        };
      }
    } catch (e) {}
    return null;
  });

  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem("agritrade_token") || null;
    } catch (e) {
      return null;
    }
  });

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      return Boolean(localStorage.getItem("agritrade_token") || localStorage.getItem("agritrade_user"));
    } catch (e) {
      return false;
    }
  });

  const [loading, setLoading] = useState(false);
  
  const [globalOrders, setGlobalOrders] = useState(() => {
    try {
      const savedUserStr = localStorage.getItem("agritrade_user");
      const savedUser = savedUserStr ? JSON.parse(savedUserStr) : null;
      const userKey = savedUser?.uuid || savedUser?.id;
      if (userKey) {
        const cached = localStorage.getItem(`wiredan_cached_orders_${userKey}`);
        return cached ? JSON.parse(cached) : [];
      }
      return [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    const userKey = user?.uuid || user?.id;
    if (userKey && globalOrders && Array.isArray(globalOrders)) {
      try {
        localStorage.setItem(`wiredan_cached_orders_${userKey}`, JSON.stringify(globalOrders));
      } catch (e) {}
    }
  }, [globalOrders, user?.uuid, user?.id]);

  const syncAllData = useCallback(async (customToken, customUserId) => {
    const currentToken = customToken || token || localStorage.getItem("agritrade_token");
    const savedUserStr = localStorage.getItem("agritrade_user");
    let parsedSavedUser = {};
    try {
      parsedSavedUser = savedUserStr ? JSON.parse(savedUserStr) : {};
    } catch (e) {}
    
    const activeUserId = customUserId || parsedSavedUser?.uuid || parsedSavedUser?.id || localStorage.getItem("userId") || localStorage.getItem("userUuid");
    if (!activeUserId || activeUserId === "local_user") return;
    
    const authHeaders = currentToken ? { 'Authorization': `Bearer ${currentToken}` } : {};

    try {
      // 🛡️ 1. Fetch Fresh Identity & Premium Balance directly from /api/user/me
      const profileRes = await fetch(`/api/user/me?id=${encodeURIComponent(activeUserId)}`, {
        headers: authHeaders,
        credentials: "include"
      });

      if (profileRes.ok) {
        const profileData = await profileRes.json();
        if (profileData && profileData.success) {
          setUser((prevUser) => {
            const baseUser = prevUser || parsedSavedUser;
            const resolvedWallet = profileData.wallet_address || profileData.sidra_wallet || baseUser?.wallet_address || "";
            const computedDan = Number(profileData.dan_balance ?? baseUser?.dan_balance ?? 0);
            const premiumFlag = (computedDan >= 2000) ? 1 : 0;

            const sanitizedUser = {
              ...baseUser,
              ...profileData,
              uuid: profileData.uuid || profileData.id || activeUserId,
              id: profileData.id || profileData.uuid || activeUserId,
              wallet_address: resolvedWallet,
              sidra_wallet: resolvedWallet,
              sidra_wallet_address: resolvedWallet,
              kyc_verified: Number(profileData.kyc_verified ?? baseUser?.kyc_verified) || 0,
              kyc_status: profileData.kyc_status || baseUser?.kyc_status || "unverified",
              balance: Number(profileData.balance ?? baseUser?.balance) || 0,
              dan_balance: computedDan,
              sda_balance: Number(profileData.sda_balance ?? baseUser?.sda_balance) || 0,
              is_premium: premiumFlag,
              can_trade_high_value: premiumFlag,
            };
            try {
              localStorage.setItem("agritrade_user", JSON.stringify(sanitizedUser));
            } catch (err) {}
            return sanitizedUser;
          });
        }
      }

      // 🛡️ 2. Fetch User Trades
      let res = await fetch(`/api/orders/my-trades?userId=${encodeURIComponent(activeUserId)}`, {
        headers: authHeaders,
        credentials: "include"
      });

      if (!res.ok) {
        res = await fetch(`/api/orders/my-trades.js?userId=${encodeURIComponent(activeUserId)}`, {
          headers: authHeaders,
          credentials: "include"
        });
      }

      if (res.ok) {
        const data = await res.json();
        const rawOrders = data?.trades || data?.orders || data?.vaultOrders || [];
        if (Array.isArray(rawOrders)) {
          setGlobalOrders(rawOrders);
        }
      }
    } catch (err) {}
  }, [token]);

  useEffect(() => {
    if (isAuthenticated) {
      const activeToken = token || localStorage.getItem("agritrade_token");
      const activeUserId = user?.uuid || user?.id || localStorage.getItem("userId") || localStorage.getItem("userUuid");
      syncAllData(activeToken, activeUserId);
    }
  }, [isAuthenticated, token, syncAllData, user?.uuid, user?.id]);

  const login = useCallback((userData, authTokenParam) => {
    const rawUser = userData.user || userData; 
    const authToken = authTokenParam || userData.token || userData.accessToken || localStorage.getItem("agritrade_token");
    
    let existingLocalUser = {};
    try {
      const cachedUserStr = localStorage.getItem("agritrade_user");
      if (cachedUserStr) existingLocalUser = JSON.parse(cachedUserStr);
    } catch (e) {}

    const resolvedWallet = rawUser.wallet_address || rawUser.sidra_wallet || rawUser.sidra_wallet_address || existingLocalUser.wallet_address || existingLocalUser.sidra_wallet || "";
    const computedDan = Number(rawUser.dan_balance ?? existingLocalUser.dan_balance ?? 0);
    const premiumFlag = (computedDan >= 2000) ? 1 : 0;

    const sanitizedUser = {
      ...existingLocalUser,
      ...rawUser,
      uuid: rawUser.uuid || rawUser.id || existingLocalUser.uuid || existingLocalUser.id || "user_node",
      id: rawUser.id || rawUser.uuid || existingLocalUser.id || existingLocalUser.uuid || "user_node",
      wallet_address: resolvedWallet,
      sidra_wallet: resolvedWallet,
      sidra_wallet_address: resolvedWallet,
      kyc_verified: Number(rawUser.kyc_verified ?? existingLocalUser.kyc_verified) || 0,
      kyc_status: rawUser.kyc_status || existingLocalUser.kyc_status || "unverified",
      balance: Number(rawUser.balance ?? existingLocalUser.balance) || 0,
      dan_balance: computedDan,
      sda_balance: Number(rawUser.sda_balance ?? existingLocalUser.sda_balance) || 0,
      is_premium: premiumFlag,
      can_trade_high_value: premiumFlag,
    };

    setUser(sanitizedUser);
    if (authToken) setToken(authToken);
    setIsAuthenticated(true);

    try {
      localStorage.setItem("agritrade_user", JSON.stringify(sanitizedUser));
      if (authToken) localStorage.setItem("agritrade_token", authToken);
      if (sanitizedUser.id) localStorage.setItem("userId", sanitizedUser.id);
      if (sanitizedUser.uuid) localStorage.setItem("userUuid", sanitizedUser.uuid);
    } catch (e) {}

    if (sanitizedUser.uuid || sanitizedUser.id) {
      syncAllData(authToken, sanitizedUser.uuid || sanitizedUser.id);
    }
  }, [syncAllData]);

  const updateUser = useCallback((updated) => {
    setUser((prevUser) => {
      const resolvedWallet = updated.wallet_address || updated.sidra_wallet || prevUser?.wallet_address || "";
      const computedDan = Number(updated.dan_balance ?? prevUser?.dan_balance ?? 0);
      const premiumFlag = (computedDan >= 2000) ? 1 : 0;

      const newState = { 
        ...prevUser, 
        ...updated,
        wallet_address: resolvedWallet,
        sidra_wallet: resolvedWallet,
        sidra_wallet_address: resolvedWallet,
        dan_balance: computedDan,
        is_premium: premiumFlag,
        can_trade_high_value: premiumFlag,
      };
      try {
        localStorage.setItem("agritrade_user", JSON.stringify(newState));
      } catch (e) {}
      return newState;
    });
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    setGlobalOrders([]);
    try {
      localStorage.removeItem("agritrade_user");
      localStorage.removeItem("agritrade_token");
      localStorage.removeItem("userId");
      localStorage.removeItem("userUuid");
    } catch (e) {}
  }, []);

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    globalOrders,
    setGlobalOrders,
    syncAllData,
    login,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
